<template>
  <div v-if="intents && intents.length" v-click-outside="closeAllPopovers">
    <!-- Hierarchy mode: useIntents is true -->
    <template v-if="useIntents">
      <!-- Direct intent buttons (Offer Details, Track Shipment) -->
      <b-button
        v-for="intent in groupedIntents.directIntents"
        :key="'direct-btn-' + intent"
        variant="secondary"
        @click="select($event, intent)"
        :class="{
          'btn-outline-success': isOutlineButton(intent),
          'no-outline-btn': !isOutlineButton(intent)
        }"
      >
        <span>{{ getIntentLabel(intent) }}</span>
      </b-button>

      <!-- Hierarchy tier 1 buttons with popovers -->
      <div
        v-for="group in groupedIntents.hierarchyGroups"
        :key="'hierarchy-' + group.category"
        class="hierarchy-group"
      >
        <b-button
          variant="secondary"
          class="btn-outline-success hierarchy-btn"
          :class="{ active: openPopover === group.category }"
          @click.stop="togglePopover(group.category)"
        >
          <span>{{ group.label }}</span>
          <font-awesome-icon
            :icon="['fas', 'angle-down']"
            class="dropdown-icon"
            :class="{ 'dropdown-icon-open': openPopover === group.category }"
          />
        </b-button>

        <!-- Popover with tier 2 action items -->
        <transition name="popover-fade">
          <div v-if="openPopover === group.category" class="hierarchy-popover">
            <div
              v-for="intent in group.intents"
              :key="'tier2-item-' + intent"
              class="hierarchy-popover-item"
              @click="select($event, intent)"
            >
              {{ getIntentLabel(intent) }}
            </div>
          </div>
        </transition>
      </div>
    </template>

    <!-- Legacy mode: useIntents is false (3 dots menu popover) -->
    <template v-else>
      <b-button
        v-for="(intent, index) in intents"
        :key="'action-btn-' + intent"
        variant="secondary"
        @click="select($event, intent)"
        :class="{
          'd-none': !showAllIntents && intents.length > displaySize && index > (displaySize - 1),
          'item-select-btn': true,
          'btn-outline-success': isOutlineButton(intent),
          'no-outline-btn': !isOutlineButton(intent),
          'position-relative': true
        }"
      >
        <span>{{ getIntentLabel(intent) }}</span>
        <font-awesome-icon class="position-absolute" :icon="['fas', 'angle-right']"></font-awesome-icon>
      </b-button>
      <div v-if="intents.length > displaySize" class="more-options" @click.stop="showAllIntents = !showAllIntents">
        <hr class="more-options-separator d-block d-lg-none" />
        <span><template v-if="showAllIntents">Hide</template><template v-else>Show</template> More Options</span>
        <svg v-if="showAllIntents" xmlns="http://www.w3.org/2000/svg" width="13" height="8" viewBox="0 0 13 8" fill="none">
          <path d="M11.5 6.5L6.5 1.5L1.5 6.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="13" height="8" viewBox="0 0 13 8" fill="none">
          <path d="M1.5 1.5L6.5 6.5L11.5 1.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </template>
  </div>
</template>

<script>
import {IntentTypeMapping} from '@/constants'
import {isPrimaryIntent, groupIntentsByHierarchy, getIntentLabel} from '@/utils'
import {mapState} from 'vuex'

export default {
  name: 'SelectItemIntents',
  props: {
    item: Object,
    intents: {
      type: Array,
      default: function() {
        return []
      }
    },
    useIntents: {
      type: Boolean,
      default: function() {
        return false
      }
    }
  },
  data: function() {
    return {
      openPopover: null,
      showAllIntents: false
    }
  },
  computed: {
    ...mapState(['retentionAuthUrl']),
    groupedIntents: function() {
      return groupIntentsByHierarchy(this.intents)
    },
    displaySize: function() {
      return 2
    }
  },
  methods: {
    getIntentLabel: function(intent) {
      return getIntentLabel(this.item, intent)
    },
    select: function(event, intent) {
      event.stopPropagation()
      this.openPopover = null

      if (this.retentionAuthUrl) {
        window.SolvpathRetentionLite.open({
          sessionUrl: this.retentionAuthUrl,
          order_id: this.item.order.order_id,
          product_id: this.item.product.product_id,
          variant_id: this.item.variant ? this.item.variant.variant_id : null,
          intent: IntentTypeMapping[intent]
        })
      } else {
        this.$emit('select', intent)
      }
    },
    togglePopover: function(category) {
      this.openPopover = this.openPopover === category ? null : category
    },
    closeAllPopovers: function() {
      this.openPopover = null
    },
    isOutlineButton: function(intent) {
      return !isPrimaryIntent(intent)
    }
  }
}
</script>

<style scoped lang="less">
.hierarchy-group {
  position: relative;
  width: 100%;
}

.no-outline-btn {
  background: var(--theme_color);
}

.hierarchy-btn {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.dropdown-icon {
  font-size: 16px;
  transition: transform 0.2s ease, color 0.2s ease;
}

.dropdown-icon-open {
  transform: rotate(180deg);
}

.btn-outline-success.hierarchy-btn:hover /deep/ .dropdown-icon,
.btn-outline-success.hierarchy-btn.active /deep/ .dropdown-icon {
  color: #fff !important;
}

.hierarchy-btn:focus,
.hierarchy-btn:active {
  outline: none;
  box-shadow: none;
}

/* Higher specificity selector to override global .btn-outline-success:hover */
.btn-outline-success.hierarchy-btn:hover {
  background-color: white !important;
  color: var(--theme_color) !important;
  border-color: var(--theme_color) !important;
}

/* Active state when dropdown is open */
.btn-outline-success.hierarchy-btn.active,
.btn-outline-success.hierarchy-btn.active:hover,
.btn-outline-success.hierarchy-btn.active:focus,
.btn-outline-success.hierarchy-btn.active:active {
  background-color: var(--theme_color) !important;
  color: #fff !important;
  border-color: var(--theme_color) !important;
  box-shadow: none !important;
}

/* Remove transitions on mobile */
@media (max-width: 768px) {
  .btn-outline-success.hierarchy-btn {
    transition: none !important;
  }
}

.hierarchy-popover {
  position: absolute;
  top: 100%;
  right: 0;
  width: 280px;
  z-index: 100;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 0;
  margin-top: 4px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
  }
}

.hierarchy-popover-item {
  padding: 10px 16px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  color: var(--theme_color);
  text-decoration: underline;
  transition: background-color 0.15s ease;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: color-mix(in srgb, var(--theme_color) 10%, transparent);
  }
}

.popover-fade-enter-active,
.popover-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.popover-fade-enter,
.popover-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
